import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../utils/ThemeSwitcher';
import { ArrowRight, Check, Star, Sun, Moon } from 'lucide-react';
import logoSource from '../assets/dataquadio.png';
import logoSourceInverted from '../assets/dataquadio_inverted.png';
import { buildPalette, getPlans, Footer } from './DataQuadLanding';
import './DataQuadLanding.css';
 
const faqs = [
  { q: 'How does the free trial work?',
    a: 'Every plan starts with a 15-day free trial — no credit card required. You get full access to your plan\u2019s features and can upgrade, downgrade, or walk away at any point during the trial.' },
  { q: 'What does "projects per month" mean?',
    a: 'A project is the home for one dataset\u2019s quality workflow — its surveys, checks, and cleaned outputs. Standard includes 1 active project per month, Pro includes 3, and Max is unlimited.' },
  { q: 'What is your refund policy?',
    a: 'All plans include a 14-day refund policy. If DataQuad isn\u2019t the right fit, contact support@noblesyntax.com within 14 days of payment for a full refund. Payments are processed securely by Paddle.' },
  { q: 'Can I switch between monthly and annual billing?',
    a: 'Yes — you can change your billing cycle from Account & Subscription at any time. Annual billing saves roughly 25% compared to paying monthly.' },
];
 
const PricingPage = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [isAnnualBilling, setIsAnnualBilling] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
 
  const c = buildPalette(isDarkMode);
  const plans = getPlans(isAnnualBilling);
 
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
 
  useEffect(() => { window.scrollTo(0, 0); }, []);
 
  return (
    <div className="dq-landing" data-theme={isDarkMode ? 'dark' : 'light'}
         style={{ background: c.bg, color: c.text, minHeight: '100vh' }}>
 
      {/* ── NAVBAR (mirrors the landing) ─────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '0 24px',
        background: scrolled
          ? (isDarkMode ? 'rgba(8,9,11,0.92)' : 'rgba(255,255,255,0.92)')
          : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${c.border}` : '1px solid transparent',
        transition: 'all 0.3s ease',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', color: c.text }}>
            <img src={isDarkMode ? logoSourceInverted : logoSource} alt="DataQuad" style={{ width: '26px', height: '26px' }} />
            <span style={{ fontSize: '17px', fontWeight: '600', letterSpacing: '-0.3px' }}>dataQuad</span>
          </a>
 
          <div className="dq-nav-links" style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
            <a href="/#features" className="dq-nav-link"
               style={{ color: c.textSecondary, fontSize: '14px', fontWeight: '500', textDecoration: 'none' }}>Features</a>
            <a href="/pricing" className="dq-nav-link"
               style={{ color: c.text, fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>Pricing</a>
            <a href="/download" className="dq-nav-link"
               style={{ color: c.textSecondary, fontSize: '14px', fontWeight: '500', textDecoration: 'none' }}>Download</a>
          </div>
 
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={toggleTheme} aria-label="Toggle theme" style={{
              background: 'none', border: 'none', color: c.textMuted, cursor: 'pointer',
              padding: '6px', display: 'flex', borderRadius: '8px', transition: 'background .2s, color .2s',
            }}>
              {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={() => navigate('/login')} style={{
              background: 'none', border: 'none', color: c.textSecondary, fontSize: '14px', fontWeight: '500',
              cursor: 'pointer', fontFamily: 'inherit', padding: '8px 12px', whiteSpace: 'nowrap',
            }}>Sign in</button>
            <button onClick={() => navigate('/login?signup=1')} className="dq-cta-btn" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: c.accent, color: '#FFFFFF', border: 'none',
              padding: '9px 20px', borderRadius: '10px', fontSize: '14px',
              fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}>Get Started</button>
          </div>
        </div>
      </nav>
 
      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section style={{ padding: '150px 24px 56px', textAlign: 'center' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <span className="dq-kicker">Pricing</span>
          <h1 style={{
            fontSize: 'clamp(34px, 4.6vw, 60px)', fontWeight: '700',
            letterSpacing: '-1.8px', lineHeight: 1.08,
            margin: '14px 0 16px', color: c.text, textWrap: 'balance',
          }}>
            Simple, transparent pricing.
          </h1>
          <p style={{ fontSize: '17px', color: c.textSecondary, margin: '0 auto 32px', maxWidth: '520px', lineHeight: 1.65 }}>
            Start with a 15-day free trial — no credit card required. Upgrade when you're ready.
          </p>
 
          {/* Billing toggle — segmented control */}
          <div style={{
            display: 'inline-flex', padding: '3px', gap: '2px',
            background: c.bgChip, border: `1px solid ${c.border}`,
            borderRadius: '11px',
          }}>
            {[
              { annual: false, label: 'Monthly' },
              { annual: true,  label: 'Annual' },
            ].map(({ annual, label }) => {
              const isOn = isAnnualBilling === annual;
              return (
                <button key={label} onClick={() => setIsAnnualBilling(annual)} style={{
                  padding: '8px 20px', borderRadius: '8px', border: 'none',
                  background: isOn ? (isDarkMode ? '#17181a' : '#FFFFFF') : 'transparent',
                  color: isOn ? c.text : c.textSecondary,
                  boxShadow: isOn
                    ? (isDarkMode ? '0 1px 2px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.08), 0 1px 6px rgba(0,0,0,0.06)')
                    : 'none',
                  fontSize: '13px', fontWeight: isOn ? 600 : 500, fontFamily: 'inherit', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  transition: 'all .2s',
                }}>
                  {label}
                  {annual && (
                    <span style={{
                      fontSize: '10px', fontWeight: '700', padding: '2px 7px',
                      borderRadius: '100px',
                      background: 'rgba(37,99,235,0.12)', color: c.accent,
                    }}>-25%</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>
 
      {/* ── PLANS ────────────────────────────────────────────────────── */}
      <section style={{ padding: '0 24px 48px' }}>
        <div style={{
          maxWidth: '1100px', margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))',
          gap: '20px',
        }}>
          {plans.map((plan) => {
            const PlanIcon = plan.icon;
            return (
              <div key={plan.key} className="dq-plan-card" style={{
                position: 'relative', padding: '32px 28px', borderRadius: '18px',
                background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
                border: `1px solid ${plan.popular ? plan.color + '40' : c.border}`,
                boxShadow: plan.popular
                  ? (isDarkMode ? '0 8px 32px rgba(0,0,0,0.35)' : '0 1px 2px rgba(0,0,0,0.04), 0 12px 32px rgba(0,0,0,0.07)')
                  : 'none',
              }}>
                {plan.popular && (
                  <div style={{
                    position: 'absolute', top: '-10px', right: '20px',
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '4px 12px', borderRadius: '100px',
                    fontSize: '11px', fontWeight: '700',
                    background: plan.color, color: '#FFF',
                  }}>
                    <Star size={10} /> Popular
                  </div>
                )}
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px',
                  background: `${plan.color}1f`, color: plan.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '18px',
                }}>
                  <PlanIcon size={20} />
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px', color: c.text }}>
                  {plan.name}
                </div>
                <div className="dq-mono" style={{ fontSize: '13px', color: c.textMuted, marginBottom: '20px' }}>
                  {plan.projects} project{plan.projects !== '1' && plan.projects !== '∞' ? 's' : ''}/mo
                </div>
                <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <span style={{
                    fontSize: '44px', fontWeight: '700',
                    letterSpacing: '-1.5px', color: c.text,
                  }}>${plan.price}</span>
                  <span style={{ fontSize: '14px', color: c.textMuted }}>/mo</span>
                </div>
                <div className="dq-mono" style={{ fontSize: '11px', color: c.textMuted, marginBottom: '22px', minHeight: '14px' }}>
                  {isAnnualBilling ? 'billed annually' : 'billed monthly'}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px',
                             display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {plan.features.map((f, fi) => (
                    <li key={fi} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      fontSize: '14px', color: c.textSecondary,
                    }}>
                      <Check size={14} style={{ color: plan.color, flexShrink: 0 }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate('/login?signup=1')} className={plan.popular ? 'dq-cta-btn' : undefined} style={{
                  display: 'flex', width: '100%', padding: '12px',
                  alignItems: 'center', justifyContent: 'center', gap: '8px',
                  fontSize: '14px', fontWeight: '600', fontFamily: 'inherit',
                  color: plan.popular ? '#FFF' : c.accent,
                  background: plan.popular
                    ? c.accent
                    : (isDarkMode ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.08)'),
                  border: 'none', borderRadius: '10px', cursor: 'pointer',
                  textAlign: 'center', transition: 'background .2s',
                }}>
                  Start Free Trial {plan.popular && <ArrowRight size={15} />}
                </button>
              </div>
            );
          })}
        </div>
 
        <p className="dq-mono" style={{
          textAlign: 'center', marginTop: '22px', fontSize: '13px', color: c.textMuted,
        }}>
          All plans include a 14-day refund policy · Payments secured by Paddle
        </p>
      </section>
 
      {/* ── EVERY PLAN INCLUDES ──────────────────────────────────────── */}
      <section style={{
        padding: '72px 24px',
        background: c.bgAlt,
        borderTop: `1px solid ${c.border}`,
        borderBottom: `1px solid ${c.border}`,
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <span className="dq-kicker">Included everywhere</span>
          <h2 style={{
            fontSize: 'clamp(24px, 2.8vw, 36px)', fontWeight: '700',
            letterSpacing: '-1px', lineHeight: 1.15,
            margin: '12px 0 36px', color: c.text,
          }}>
            Every plan ships the full pipeline.
          </h2>
          <div style={{
            display: 'grid', gap: '14px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            textAlign: 'left',
          }}>
            {[
              'KoBoToolbox, SurveyCTO & ODK connections',
              'Excel, CSV, Stata & SQLite file loading',
              '15-day free trial — no credit card',
              'Interactive quality dashboards',
              'Cleaning Studio with tracked edits',
              'PDF quality reports & data exports',
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                padding: '14px 16px', borderRadius: '12px',
                background: c.bg, border: `1px solid ${c.border}`,
                fontSize: '13.5px', color: c.textSecondary, lineHeight: 1.5,
              }}>
                <Check size={15} style={{ color: c.accent, flexShrink: 0, marginTop: '2px' }} />
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>
 
      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <section style={{ padding: '88px 24px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <span className="dq-kicker">FAQ</span>
            <h2 style={{
              fontSize: 'clamp(24px, 2.8vw, 36px)', fontWeight: '700',
              letterSpacing: '-1px', lineHeight: 1.15,
              margin: '12px 0 0', color: c.text,
            }}>
              Questions, answered.
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} style={{
                  borderRadius: '14px',
                  background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
                  border: `1px solid ${c.border}`,
                  overflow: 'hidden',
                }}>
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      width: '100%', padding: '16px 20px', gap: '14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontFamily: 'inherit', textAlign: 'left',
                      fontSize: '14.5px', fontWeight: '600', color: c.text,
                    }}>
                    {f.q}
                    <span aria-hidden style={{
                      color: c.textMuted, fontSize: '18px', fontWeight: 400, flexShrink: 0,
                      transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
                      transition: 'transform .25s ease', lineHeight: 1,
                    }}>+</span>
                  </button>
                  <div style={{
                    maxHeight: open ? '220px' : '0',
                    opacity: open ? 1 : 0,
                    overflow: 'hidden',
                    transition: 'max-height .3s ease, opacity .25s ease',
                  }}>
                    <p style={{
                      margin: 0, padding: '0 20px 18px',
                      fontSize: '13.5px', lineHeight: 1.7, color: c.textSecondary,
                    }}>
                      {f.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
 
      {/* ── CLOSER ───────────────────────────────────────────────────── */}
      <section style={{
        padding: '96px 24px', textAlign: 'center',
        background: c.bgAlt,
        borderTop: `1px solid ${c.border}`,
      }}>
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(28px, 3.4vw, 44px)', fontWeight: '700',
            letterSpacing: '-1.2px', lineHeight: 1.1,
            margin: '0 0 26px', color: c.text,
          }}>
            Get started today.
          </h2>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/login?signup=1')} className="dq-cta-btn" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: c.accent, color: '#FFFFFF', border: 'none',
              padding: '14px 32px', borderRadius: '12px', fontSize: '16px',
              fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Start Free Trial <ArrowRight size={18} />
            </button>
            <a href="/download" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: c.bgChip, color: c.text, border: `1px solid ${c.border}`,
              padding: '14px 32px', borderRadius: '12px', fontSize: '16px',
              fontWeight: '500', textDecoration: 'none', fontFamily: 'inherit',
              transition: 'border-color .2s',
            }}>
              Download
            </a>
          </div>
        </div>
      </section>
 
      <Footer isDarkMode={isDarkMode} c={c}
              logoSource={logoSource} logoSourceInverted={logoSourceInverted} />
    </div>
  );
};
 
export default PricingPage;